import React, {useCallback, useMemo} from "react";
import {MediaWithCategories, StoredMediaCategory} from "../../../../types/Media";
import {useGlobalContext} from "../../../../contexts/globalContext";
import cx from "classnames";
import styles from "./EditMediaModalContent.module.scss";
import Input from "../../../../components/Input";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import Button from "../../../../components/Button";
import {useTranslation} from "react-i18next";
import Heading from "../../../../components/Heading";
import {useUpdateData} from "../../../../hooks/useUpdateData";
import CategoryMediaSelect from "../CategoryMediaSelect";
import {getRecParents} from "../../../../helpers/getRecParents";
import * as yup from "yup";

const EditMediaModalContent: React.FC<{ media: MediaWithCategories, onSubmit: () => void}> = ({
  media,
  onSubmit,
}) => {
    const { closeModal, showToast } = useGlobalContext()
    const { t } = useTranslation(['settings']);
    const storeMediaSchema = useMemo(() => {
        return ({
            name: yup.string().required().min(3),
            media_category_ids: yup.array().required().min(2)
        })
    },[])

    const {
        element,
        changeProp,
        submit,
        isValid
    } = useUpdateData<MediaWithCategories>('media', media, media.id, storeMediaSchema)

    const toggleCategory = useCallback((value: number, tree: StoredMediaCategory[]) => {
        if (element.media_category_ids?.includes(value)) {
            const unique = [...new Set(element.media_category_ids.filter((id) => id !== value))]
            changeProp({media_category_ids: unique})
        } else {
            const unique = [...new Set([...element.media_category_ids, ...getRecParents(value, tree)])]
            changeProp({media_category_ids: unique})
        }
    }, [changeProp, element.media_category_ids])

    const handleSubmit = () => {
        submit().then((res) => {
            if (res.status === 200) {
                closeModal()
                showToast('top-right', 'success', t('media.edit_modal.success'))
            } else {
                showToast('top-right', 'error', t('media.edit_modal.error'))
            }
            onSubmit()
        })
    }

    return (
        <div className={cx(styles['zr-modal-wrapper'], styles['scrollable-modal'])}>
            <div className={cx(styles['zr-media-content-wrapper'])}>
                <Heading level={4}>{t('media.edit_modal.sub_heading')}</Heading>
                <Input
                    type={'text'} placeholder={t('media.labels.title')}
                    property={'name'}
                    value={element.name}
                    onChange={(property, value) => changeProp({name: value})}
                    style={{margin: 0}}
                />
                <div className={'float-left'}>
                    <ToggleSwitch
                        property={'active'}
                        value={element.active}
                        label={t('media.labels.active')}
                        onChange={(property, value) => changeProp({active: value})}
                    />
                </div>
                <Button
                    hierarchy={'primary'}
                    text={t("update")}
                    fn={() => handleSubmit()}
                    classes={'w-100 mb-2 '}
                    disabled={!isValid}
                />
            </div>
            <CategoryMediaSelect
                selectedCategoryIds={element.media_category_ids}
                onToggle={toggleCategory}
            />
        </div>
    );
}

export default EditMediaModalContent;
